import { Link } from 'react-router-dom';
import { useParentAccess } from '../../school/schoolApi';
import RowMenu from '../../components/RowMenu';
import ConfirmDialog from '../../components/ConfirmDialog';
import PackagePicker from '../../packages/PackagePicker';
import { REWARD_ICONS, type RewardIcon } from '../../settings/SettingsContext';
import type { HomeModel } from './useHome';
import { useT } from '../../i18n/i18n';

/**
 * How many rewards this program has earned, drawn rather than counted.
 *
 * Past a handful the glyphs stop being countable at a glance and start being a wall, so the
 * rest becomes a number — three stars read as three, twenty stars read as "lots".
 */
export function RewardMarks({ total, icon, name }: { total: number; icon: string; name: string }) {
  const t = useT();
  if (total <= 0) return null;
  const glyph = REWARD_ICONS[icon as RewardIcon] ?? REWARD_ICONS.star;
  const shown = Math.min(total, 5);
  return (
    <span className="reward-marks" aria-label={t('{n} {reward}', { n: total, reward: name })}>
      <span aria-hidden="true">{glyph.repeat(shown)}</span>
      {total > shown && <span className="more" aria-hidden="true">×{total}</span>}
    </span>
  );
}

export function AddProgramsMenu({ h, text }: { h: HomeModel; text?: string }) {
  const t = useT();
  const allowed = useParentAccess();
  if (!allowed.edit) return null;
  return (
    <RowMenu label={t('Add programs')} text={text ?? t('Add programs')} primary alignStart>
      <Link
        className="usermenu-item"
        role="menuitem"
        to={`/programs/new${h.learnerId ? `?learner=${encodeURIComponent(h.learnerId)}` : ''}`}
      >
        {t('Create a new program')}
      </Link>
      <Link className="usermenu-item" role="menuitem" to="/library">
        {t('Add from the library')}
      </Link>
      {h.settings.features.programPackages && (
        <button type="button" className="usermenu-item" role="menuitem" onClick={h.openApplySet}>
          {t('Add a set of programs')}
        </button>
      )}
    </RowMenu>
  );
}

export function HomeDialogs({ h }: { h: HomeModel }) {
  const t = useT();
  return (
    <>
      <PackagePicker
        open={h.pickerMode !== null}
        title={h.pickerMode === 'add' ? t('Add to a set') : t('Add a set of programs')}
        message={
          h.pickerMode === 'add'
            ? t('"{program}" will be copied into the set as it is now.', { program: h.pickerTarget?.name ?? '' })
            : t('Every program in the set will be added for this learner.')
        }
        packages={h.packages}
        allowCreate={h.pickerMode === 'add'}
        confirmLabel={h.pickerMode === 'add' ? t('Add to set') : t('Add these programs')}
        busy={h.pickerBusy}
        error={h.pickerError}
        onPick={(id) => {
          void (h.pickerMode === 'add' ? h.addToPackage(id) : h.applyToLearner(id));
        }}
        onCreate={(name) => { void h.createThenAdd(name); }}
        onCancel={h.closePicker}
      />

      <ConfirmDialog
        open={h.pendingDelete !== null}
        title={t('Delete program?')}
        message={
          h.pendingDelete
            ? t('"{program}" will be removed. This cannot be undone.', { program: h.pendingDelete.name })
            : ''
        }
        confirmLabel={h.deleting ? t('Deleting…') : t('Delete')}
        cancelLabel={t('Keep')}
        destructive
        onConfirm={h.confirmDelete}
        onCancel={h.cancelDelete}
      />
    </>
  );
}
